import type React from "react";
import type { z } from "zod";

/**
 * CartAssistant tool/component contracts used by src/lib/cartAssistant/registry.ts.
 * Tools execute in the browser; only their name/description/input schema are
 * sent to the model.
 */
export interface CartAssistantTool<I = any, O = any> {
  name: string;
  description: string;
  inputSchema: z.ZodType<I>;
  outputSchema?: z.ZodType<O>;
  tool: (input: I) => Promise<O> | O;
}

export interface CartAssistantComponent {
  name: string;
  description: string;
  component: React.ComponentType<any>;
  propsSchema: z.ZodType<any>;
}

export const defineTool = <I, O>(tool: CartAssistantTool<I, O>): CartAssistantTool<I, O> => tool;

/**
 * Block-based message shape consumed by the chat UI and generative cards.
 * Produced from AI SDK UIMessages by toLegacyMessage().
 */
export type LegacyTextContent = { type: "text"; text: string };
export type LegacyComponentContent = {
  type: "component";
  id: string;
  name: string;
  props: Record<string, any>;
  streamingState: "started" | "streaming" | "done";
  renderedComponent?: React.ReactNode;
};
export type LegacyToolUseContent = {
  type: "tool_use";
  id: string;
  name: string;
  input: any;
  hasCompleted: boolean;
  statusMessage?: string;
};
// Block types the renderer still guards against; never produced by toLegacyMessage().
export type LegacyResourceContent = { type: "resource"; resource?: { uri?: string; url?: string; blob?: string; mimeType?: string } };
export type LegacyToolResultContent = { type: "tool_result" };
export type LegacyContent =
  | LegacyTextContent
  | LegacyComponentContent
  | LegacyToolUseContent
  | LegacyResourceContent
  | LegacyToolResultContent;

export interface LegacyThreadMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: LegacyContent[];
  createdAt?: string;
  reasoning?: string[];
}

export interface AssistantThreadSummary {
  id: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  messages?: LegacyThreadMessage[];
}

export interface AssistantSuggestion {
  id: string;
  title: string;
  detailedSuggestion: string;
}
